import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useGetClaimPrintPreviewMutation } from '@/store/api/saleClaimApi'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: Sale Control "Claim" print. frmCtrlSalesQryClaim_Choose collects
// two free-text inputs (Claimable At / Notion) BEFORE printing, then opens
// rpCtrlSalesClaimCheckWeight (Weight claims) or rpCtrlSalesClaimCheckOthers
// (Others / Quality claims) — the BE resolves the branch from the row
// claim_type. This dialog is the chooser + preview in one: two TextFields + a
// "Print Preview" button → useGetClaimPrintPreviewMutation → iframe srcDoc
// preview + Print (hidden when !canEdit) → printAndUploadReport. Responsive A4
// fit + mobile-stacked inputs (skill /responsive-style + /convert-pr).

interface ClaimPrintDialogProps {
  open: boolean
  claimId: number | null
  claimScsCode: string | null
  canEdit?: boolean
  onClose: () => void
}

// Override CSS to fit A4 inside the scaled iframe (same set as DoPrintDialog).
const PRINT_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 11mm !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 200mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; }
.access-report { position: relative !important; margin: 0 auto !important; background: #fff !important; }
.ctl { position: absolute !important; overflow: visible !important; line-height: 1.2 !important; }
.ctl.bold { font-weight: 700 !important; }
.ctl.align-center { text-align: center !important; }
.ctl.align-right { text-align: right !important; }
.ctl img { width: 100% !important; height: 100% !important; object-fit: contain !important; object-position: left top !important; display: block !important; }
.ctl.logo { overflow: hidden !important; }
`.trim()

function buildPreviewHtml(rawHtml: string): string {
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="claim-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function ClaimPrintDialog({
  open,
  claimId,
  claimScsCode,
  canEdit = true,
  onClose,
}: ClaimPrintDialogProps) {
  const [claimableAt, setClaimableAt] = useState('')
  const [notion, setNotion] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [getPreview, { data, isLoading, reset }] = useGetClaimPrintPreviewMutation()

  const iframeTitle = 'Claim Print Preview'
  const html = data?.html ?? ''
  const previewHtml = html ? buildPreviewHtml(html) : ''

  // Reset chooser inputs + preview each time the dialog opens for a new claim.
  useEffect(() => {
    if (open) {
      setClaimableAt('')
      setNotion('')
      reset()
    }
  }, [open, claimId, reset])

  const handlePreview = () => {
    if (claimId == null) return
    void getPreview({ claimId, claimableAt, notion })
      .unwrap()
      .catch((err) => {
        const detail = err?.data?.detail
        setToastMessage(typeof detail === 'string' ? detail : 'Cannot generate print preview.')
      })
  }

  const title = `Claim${claimScsCode ? ` (${claimScsCode})` : ''}`

  return (
    <>
      {open && isLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      {/* Phase 1 — chooser (Claimable At / Notion) before printing. */}
      <Dialog open={open && !html} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
            <TextField
              label="Claimable At"
              value={claimableAt}
              onChange={(e) => setClaimableAt(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Notion"
              value={notion}
              onChange={(e) => setNotion(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={claimId == null || isLoading}
            onClick={handlePreview}
          >
            Print Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 2 — A4 preview iframe + Print. */}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => reset()} size="small">
              Back
            </Button>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const printTitle = `${sanitizePdfFileNamePart(claimScsCode || 'claim')}_Ctrl_Sales_Claim.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesClaim',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
